import type { QueryDocumentSnapshot } from 'firebase/firestore'
import type { Order, OrderReturnRequest } from '@/types'
import type { FirestorePageResult } from '@/hooks/useFirestorePagination'

const API_BASE = import.meta.env.VITE_API_URL || ''

async function parseJson<T>(res: Response): Promise<T> {
  return (await res.json().catch(() => ({}))) as T
}

export async function fetchStoreOrdersByContactPage(
  email: string,
  phone: string,
  opts: {
    pageSize: number
    startAfter: QueryDocumentSnapshot | null
  }
): Promise<FirestorePageResult<Order>> {
  const page = opts.startAfter ? Number(opts.startAfter.id) + 1 : 1
  const params = new URLSearchParams({
    email,
    phone,
    page: String(page),
    pageSize: String(opts.pageSize),
  })
  const res = await fetch(`${API_BASE}/api/store/orders?${params}`)
  const data = await parseJson<{
    ok?: boolean
    orders?: Order[]
    hasMore?: boolean
    error?: string
  }>(res)
  if (!res.ok || data.ok === false) {
    throw new Error(data.error || 'Failed to load orders')
  }
  const orders = data.orders ?? []
  return {
    items: orders,
    lastDoc: data.hasMore ? ({ id: String(page) } as QueryDocumentSnapshot) : null,
    hasMore: data.hasMore === true,
  }
}

export async function countStoreOrdersByContact(
  email: string,
  phone: string
): Promise<number> {
  const params = new URLSearchParams({
    email,
    phone,
    page: '1',
    pageSize: '1',
  })
  const res = await fetch(`${API_BASE}/api/store/orders?${params}`)
  const data = await parseJson<{ ok?: boolean; total?: number; error?: string }>(
    res
  )
  if (!res.ok || data.ok === false) {
    throw new Error(data.error || 'Failed to count orders')
  }
  return data.total ?? 0
}

export async function fetchStoreOrderForContact(
  orderId: string,
  email: string,
  phone: string
): Promise<Order | null> {
  const params = new URLSearchParams({ email, phone })
  const res = await fetch(
    `${API_BASE}/api/store/orders/${encodeURIComponent(orderId)}?${params}`
  )
  const data = await parseJson<{
    ok?: boolean
    order?: Order
    error?: string
  }>(res)
  if (res.status === 404) return null
  if (!res.ok || data.ok === false) {
    throw new Error(data.error || 'Failed to load order')
  }
  return data.order ?? null
}

export async function fetchStoreOrderReturnRequest(
  orderId: string,
  email: string,
  phone: string
): Promise<OrderReturnRequest | null> {
  const params = new URLSearchParams({ email, phone })
  const res = await fetch(
    `${API_BASE}/api/store/orders/${encodeURIComponent(orderId)}/return-request?${params}`
  )
  const data = await parseJson<{
    ok?: boolean
    request?: OrderReturnRequest | null
    error?: string
  }>(res)
  if (res.status === 404) return null
  if (!res.ok || data.ok === false) {
    throw new Error(data.error || 'Failed to load return request')
  }
  return data.request ?? null
}

export async function fetchBestSellerProductIds(
  limit = 4,
  timeoutMs = 8_000
): Promise<string[]> {
  try {
    const controller = new AbortController()
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs)
    const params = new URLSearchParams({ limit: String(limit) })
    const res = await fetch(`${API_BASE}/api/store/best-sellers?${params}`, {
      signal: controller.signal,
    })
    window.clearTimeout(timeoutId)
    const data = await parseJson<{
      ok?: boolean
      productIds?: string[]
      error?: string
    }>(res)
    if (!res.ok || data.ok === false) {
      return []
    }
    return data.productIds ?? []
  } catch {
    return []
  }
}
